import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { clean, shortName, toParagraph } from '../utils/quinn';
import { useRawProperty } from '../utils/hooks';
import { PATHS } from '../utils/site';
import { photoTiles } from '../utils/gallery';

const homesCtaDef: SlotGroupDefinition = {
  label: 'Available Homes Button',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'View Available Homes' },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_online_leasing_url',
      suggestedFields: ['property_online_leasing_url', 'property_apply_now_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const contactCtaDef: SlotGroupDefinition = {
  label: 'Contact Button',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Contact Us' },
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const photoDef: SlotGroupDefinition = {
  label: 'Photo',
  description: 'Until a photo is picked, shows a home exterior from the gallery, else the featured image.',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text', defaultValue: '' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: "A gallery photo of a home's exterior at this property: the front of a house, townhome or building residents live in. Not an amenity, an interior, or the neighborhood. Leave empty if the gallery has no exterior shot.",
    context: 'Large photo beside the "Tour on Your Time" headline on the Self-Guided Tour page, cropped to a wide landscape frame.',
  },
};

/** Self-guided tour hero (Paper 11): the promise, two ways forward, one photo of a home. */
export function TourHero() {
  const p = useRawProperty();
  const homes = useSlotGroup('tourhero.homes_cta', homesCtaDef);
  const contact = useSlotGroup('tourhero.contact_cta', contactCtaDef);
  const photo = useSlotGroup('tourhero.photo', photoDef);
  const name = shortName(clean(p.property_name));

  const tiles = photoTiles((p.photo_gallery as []) || []);
  const overridden = !!photo.image && photo.image !== tiles[0]?.src;
  const pick = tiles.find((t) => /exterior|home|house|townhome|front porch/i.test(t.alt) && t.cat !== 'neighborhood') || null;
  const featured = clean(p.property_featured_image);
  const img = overridden ? { src: photo.image!, alt: photo.alt || '' } : pick ? { src: pick.src, alt: pick.alt } : featured ? { src: featured, alt: '' } : tiles[0] ? { src: tiles[0].src, alt: tiles[0].alt } : null;
  const homesHref = homes.url || clean(p.property_online_leasing_url) || PATHS.floorPlans;

  return (
    <section className="page-hero tourhero">
      <div className="page-hero__copy" data-reveal>
        <RichTextContent path="tourhero.eyebrow" as="div" className="eyebrow" defaultValue={toParagraph(['Self-guided tour', name].filter(Boolean).join(' · '))} />
        <RichTextContent path="tourhero.title" as="h1" className="h1" />
        <RichTextContent path="tourhero.lead" as="p" className="page-hero__lead" />
        <div className="flex gap-3 pt-1 max-[767px]:flex-col max-[767px]:gap-2.5">
          <EditableSlotGroup groupPath="tourhero.homes_cta" definition={homesCtaDef}>
            <a className="btn btn-primary" href={homesHref} target={homes._target || '_self'}>
              {homes.label || 'View Available Homes'}
            </a>
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="tourhero.contact_cta" definition={contactCtaDef}>
            <a className="btn btn-secondary" href={contact.url || PATHS.contact} target={contact._target || '_self'}>
              {contact.label || 'Contact Us'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>
      <EditableSlotGroup groupPath="tourhero.photo" definition={photoDef} className={img ? 'photo page-hero__photo' : 'page-hero__photo--empty'}>
        {img && <VibesitesImage url={img.src} alt={img.alt} priority sizes="(min-width: 1200px) 45vw, 100vw" />}
      </EditableSlotGroup>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'TourHero',
  displayName: 'Self-Guided Tour Hero',
  tokens: {
    'tourhero.eyebrow': { label: 'Eyebrow', description: 'Until edited, shows "Self-guided tour · " and the community name.', type: 'richtext', defaultValue: '<p>Self-guided tour</p>' },
    'tourhero.title': { label: 'Title', type: 'richtext', defaultValue: '<p>Tour on<br>Your Time.</p>', suggestedFields: ['property_scheduletour_blurb'] },
    'tourhero.lead': { label: 'Lead', type: 'richtext', defaultValue: '<p>Explore your future home at your own pace.</p>' },
  },
  slotGroups: {
    'tourhero.homes_cta': homesCtaDef,
    'tourhero.contact_cta': contactCtaDef,
    'tourhero.photo': photoDef,
  },
};

(TourHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default TourHero;
